/**
 * Dev Intake Utilities
 * 
 * Exposes window.__intakeLead for testing live routing without UI.
 * This is a development/debugging tool only.
 */

import { intakeLead, createTestLeadPayload, type IntakeOptions } from './intakeLead';
import type { IncomingLeadPayload, IntakeResult } from './leadTypes';
import type { RoutingContext } from '../routingOrchestrator';

// --- Global State for Dev Context ---

let devRoutingContext: RoutingContext | null = null;

/**
 * Set the routing context for dev intake.
 * This must be called from a React component that has access to agents/weights.
 */
export function setDevRoutingContext(context: RoutingContext | null) {
  devRoutingContext = context;
  
  if (import.meta.env.MODE !== 'production') {
    console.log('[DevIntake] Routing context', context ? 'set' : 'cleared');
  }
}

/**
 * Get the current dev routing context.
 */
export function getDevRoutingContext(): RoutingContext | null {
  return devRoutingContext;
}

// --- Dev Intake Function ---

/**
 * Process a lead through intake using the dev context.
 * Exposed as window.__intakeLead for console testing.
 */
export function devIntakeLead(
  payload: Partial<IncomingLeadPayload>,
  options: Partial<IntakeOptions> = {}
): IntakeResult {
  const fullPayload = createTestLeadPayload(payload);
  
  const result = intakeLead(fullPayload, {
    routingContext: options.routingContext ?? devRoutingContext ?? undefined,
    debug: options.debug ?? true,
    ...options,
  });
  
  // Log result in dev mode
  if (import.meta.env.MODE !== 'production') {
    console.log('[DevIntake] Result:', {
      success: result.success,
      leadId: result.leadId,
      status: result.intakeStatus,
      outcome: result.routingOutcome,
      agent: result.assignedAgentName,
      charged: result.chargedAmount,
    });
  }
  
  return result;
}

/**
 * Process multiple test leads for batch testing.
 */
export function devIntakeBatch(
  count: number,
  payloadOverrides: Partial<IncomingLeadPayload> = {},
  options: Partial<IntakeOptions> = {}
): IntakeResult[] {
  const results: IntakeResult[] = [];
  
  for (let i = 0; i < count; i++) {
    const payload: Partial<IncomingLeadPayload> = {
      ...payloadOverrides,
      name: payloadOverrides.name || `Test Lead ${i + 1}`,
      phone: payloadOverrides.phone || `555-${String(i).padStart(3, '0')}-${String(1000 + i)}`,
    };
    
    results.push(devIntakeLead(payload, options));
  }
  
  // Summary
  const routed = results.filter(r => r.routingOutcome === 'ROUTED').length;
  const retried = results.filter(r => r.routingOutcome === 'RETRY_QUEUED').length;
  const stocked = results.filter(r => r.routingOutcome === 'STOCKED').length;
  const failed = results.filter(r => !r.success).length;
  
  console.log('[DevIntake] Batch complete:', {
    total: count,
    routed,
    retried,
    stocked,
    failed,
  });
  
  return results;
}

// --- Window Exposure ---

/**
 * Initialize dev intake on window for console access.
 */
export function initDevIntake() {
  if (typeof window === 'undefined') return;
  
  // Expose on window
  (window as any).__intakeLead = devIntakeLead;
  (window as any).__intakeBatch = devIntakeBatch;
  (window as any).__intakeContext = {
    get: getDevRoutingContext,
    set: setDevRoutingContext,
  };
  
  if (import.meta.env.MODE !== 'production') {
    console.log('[DevIntake] Dev utilities initialized. Available commands:');
    console.log('  window.__intakeLead({ name, phone, state, leadType, ... })');
    console.log('  window.__intakeBatch(count, overrides)');
    console.log('  window.__intakeContext.get()');
  }
}

// Auto-initialize in non-production
if (import.meta.env.MODE !== 'production') {
  initDevIntake();
}
